import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {
  COMMISSION_IMPORT_ERGO_HESTIA_IPEGAZ, COMMISSION_IMPORT_UNIQA_CORPORATION, COMMISSION_IMPORT_UNIQA_POS,
  DictService
} from '../../services/dict.service';
import {from, ReplaySubject, Subject, Subscription} from 'rxjs';
import {Dictionary} from '../../interface/dictionary';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatSelectChange} from '@angular/material/select';
import {delay, filter, finalize, first, take, takeUntil, tap} from 'rxjs/operators';

@Component({
  selector: 'app-dict',
  templateUrl: './dict.component.html',
  styleUrls: ['./dict.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictComponent)}
  ]
})
export class DictComponent implements OnInit, ControlValueAccessor, OnDestroy, OnChanges {
  @Input() dictType: string;
  @Input() dictTypeSubject: Subject<string>;
  @Input() label: string;
  @Input() multiple = false;
  @Input() returnKey = false;
  @Input() control?: FormControl = null;
  @Input() required = false;
  @Input() profit?: boolean = false;
  @Input() appearance = null;
  @Input() color = false;
  @Input() onlyKeys?: string[];
  @Input() skipKeys?: string[];
  @Input() autoSelect = true;
  @Input() disable = false;
  @Input() sortDESC = false;
  @Output() selectedDict: EventEmitter<Dictionary> = new EventEmitter<Dictionary>();
  @Output() selectedMultipleDict: EventEmitter<Dictionary[]> = new EventEmitter<Dictionary[]>();
  @Output() availableItems: EventEmitter<Dictionary[]> = new EventEmitter<Dictionary[]>();


  externalControl = true;
  disabled = false;
  items: Dictionary[] = [];
  filteredItems: Dictionary[] = null;
  value?: number = null;
  currentColor = '';
  filterControl: FormControl;
  dictTypeSubjectSub?: Subscription = null;
  profitKeys =  [
    COMMISSION_IMPORT_ERGO_HESTIA_IPEGAZ, COMMISSION_IMPORT_UNIQA_CORPORATION];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
      private dictService: DictService,
      private fb: FormBuilder,
  ) {
    this.filterControl = fb.control('');
    // this.profitKeys = [COMMISSION_IMPORT_ERGO_HESTIA_MTU_JUPITER, COMMISSION_IMPORT_ERGO_HESTIA_IPEGAZ, COMMISSION_IMPORT_ERGO_HESTIA_JUPITER_YCD];
  }

  onChange: any = () => { };
  onTouched: any = () => { };


  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl({ value: '', disabled: this.disable });
      this.externalControl = false;
    } else if (this.disable) {
      this.control.disable();
    }


    this.control.valueChanges
        .pipe(
          takeUntil(this.destroyed$),
          delay(0)
        )
        .subscribe((val: number) => {
          this.refreshCurrentColor(val);
        });

    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val?: string) => {
        if (!val) {
          this.filteredItems = null;
          return;
        }

        this.filteredItems = this.items;

        this.filteredItems = this.filteredItems.filter((dictionary: Dictionary) => {
          return dictionary.name.toUpperCase().includes(val.toUpperCase());
        });
      });
  }

  ngOnChanges(): void {
    if (this.dictTypeSubject) {
      if (this.dictTypeSubjectSub === null) {
        this.dictTypeSubjectSub = this.dictTypeSubject
          .pipe(takeUntil(this.destroyed$))
          .subscribe((dictType: string) => this.loadItems(dictType));
      }
    } else {
      this.loadItems(this.dictType);
    }
  }

  loadItems(dictType: string): void {
    this.items = [];
    this.dictService.getDictSubject()
      .pipe(
        take(1)
      )
      .subscribe((items: Dictionary[]) => {
        from(items)
          .pipe(
            filter((e: Dictionary) => e.type === dictType),
            filter((e: Dictionary) => {
              if (!this.profit){
                return true;
              }
              // Policy importer rejected:
              return !this.profitKeys.includes(e.key);
            }),
            filter((e: Dictionary) => {
              if (!this.onlyKeys) {
                return true;
              }

              return this.onlyKeys.includes(e.key);
            }),
            filter((e: Dictionary) => {
              if (!this.skipKeys) {
                return true;
              }

              return !this.skipKeys.includes(e.key);
            }),
            tap((e: Dictionary) => {
              if (!this.items.some((search: Dictionary) => search.id === e.id)) {
                this.items.push(e);
              }
            }),
            finalize(() => {
              if (this.sortDESC)
              {
                this.items = this.items.sort((a: Dictionary, b: Dictionary) => {
                  if (a.order > 0 && b.order > 0) {
                    return b.order - a.order;
                  }

                  return b.name.localeCompare(a.name);
                });
              } else {
                this.items = this.items.sort((a: Dictionary, b: Dictionary) => {
                  if (a.order > 0) {
                    return a.order - b.order;
                  }

                  return a.name.localeCompare(b.name);
                });
              }

              if (this.autoSelect && this.items.length === 1 && this.required) {
                if (this.multiple) {
                  this.control.setValue([this.items[0].id]);
                } else {
                  this.control.setValue(this.items[0].id);
                }
              }

              this.availableItems.emit(this.items);
            })
          ).subscribe();
        });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    if (this.multiple) {
      const selected = [];
      this.items.forEach((dict: Dictionary) => {
        if (e.value.includes(dict.id)) {
          selected.push(dict);
        }

        this.selectedMultipleDict.emit(selected);
      });
    } else {
      this.items.forEach((dict: Dictionary) => {
        if (dict.id === e.value) {
          this.selectedDict.emit(dict);
        }
      });
    }

    this.value = e.value;
    this.onChange(this.value);
    this.refreshCurrentColor(e.value);
  }

  refreshCurrentColor(val: number): void {
    this.dictService.getDictSubject()
      .pipe(first())
      .subscribe((items: Dictionary[]) => {
        const currentDict = items.find((dict: Dictionary) => dict.id === val);
        this.currentColor = currentDict ? currentDict.color : '';
      });
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }
}
